import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete,
  Tooltip,
  IconButton,
} from "@mui/material";
import DecisionLogicReport from "./DecisionLogicReport";
import { get, post } from "requests";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import { Close, Refresh, Save } from "@mui/icons-material";
import FullPageLoader from "components/Loader/FullPage";

interface Contact {
  id: any;
  firstName: string;
  lastName: string;
  email?: string;
}

interface Report {
  id: number;
  fullName: string;
  account_no: string;
  routing_no: string;
  status: string;
}

interface Props {
  dealApplicationId: number;
}

const DecisionLogicTab: React.FC<Props> = ({ dealApplicationId }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [bankEdit, setBankEdit] = useState(false);
  const [routing, setRouting] = useState("");
  const [account, setAccount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [currentReports, setCurrentReports] = useState<any[]>([]);
  const [currentStabilityScore, setCurrentStabilityScore] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const contactData = await get(`decisionLogic/getContacts/${dealApplicationId}`);
        const reportData: any = await get(`decisionLogic/getReportList/${dealApplicationId}`);

        setContacts(contactData);
        setReports(reportData);

        if (reportData.length > 0) {
          const firstReport = reportData[0];
          setSelectedReportId(firstReport.id.toString());
          loadReportDetails(firstReport.id);
        }
        console.log("contact Data", contactData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getStatusLabel = (status: string) => {
    const val = parseInt(status);
    return val === 0 ? "Pending" : val === 1 ? "Incomplete" : val > 1 ? "Success" : "Failed";
  };

  const loadReportDetails = async (reportId: number) => {
    try {
      const res: any = await get(`decisionLogic/getReportDetailsFromDatabase/${reportId}`);
      if (res?.result?.length > 0) {
        setCurrentReports(res.result);
        setCurrentStabilityScore(res.stabilityScore);
        setSelectedAccount({
          label: res?.result[0]?.AccountNumberFound || "No Account Number Found",
          value: 0,
        });
      } else {
        setCurrentReports([]);
        setCurrentStabilityScore(0);
        setSelectedAccount({});
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => console.log("selected Account", selectedAccount), [selectedAccount]);

  const handleSubmit = async () => {
    if (!selectedContactId) return;
    setSubmitting(true);
    try {
      await post("decisionLogic/sendRequest", {
        applicationId: dealApplicationId,
        accountNumber: account,
        routingNumber: routing,
        customerId: selectedContactId,
      });

      setShowDialog(false);
      resetForm();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setBankEdit(false);
    setRouting("");
    setAccount("");
    setSelectedContactId("");
  };

  return (
    <Box sx={{ padding: 2, color: "#685d6e" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{ bgcolor: "#bbe4e9", p: 1 }}
      >
        <Typography fontWeight={500}>DECISIONLOGIC</Typography>
        <MDButton color="primary" variant="contained" onClick={() => setShowDialog(true)}>
          Run DecisionLogic
        </MDButton>
      </Box>

      {(loading || submitting) && (
        <Box display="flex" justifyContent="center" my={4}>
          <FullPageLoader />
        </Box>
      )}

      {reports.length === 0 ? (
        <Typography color="error" fontWeight="bold" fontSize="large">
          No Reports Found
        </Typography>
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent={"space-between"} gap={2} mb={2}>
            <Select
              value={selectedReportId}
              sx={{ width: "50%", padding: "10px" }}
              size="medium"
              label="Select Report"
              onChange={(e) => {
                setSelectedReportId(e.target.value);
                loadReportDetails(parseInt(e.target.value));
              }}
            >
              {reports.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {`${r.fullName} ${r.account_no} / ${r.routing_no} - ${getStatusLabel(r.status)}`}
                </MenuItem>
              ))}
            </Select>

            <Tooltip title="Refresh All" arrow>
              <IconButton
                size={"large"}
                sx={{ marginLeft: -3, color: "#2F929F" }}
                onClick={() => loadReportDetails(parseInt(selectedReportId))}
              >
                <Refresh fontSize={"large"} />
              </IconButton>
            </Tooltip>
          </Box>

          <DecisionLogicReport
            currentReports={currentReports}
            currentStabilityScore={currentStabilityScore}
            selectedAccount={selectedAccount}
            handleSelectAccount={(opt: any) => setSelectedAccount(opt)}
          />
        </>
      )}

      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Initiate Decision Logic</DialogTitle>
        <DialogContent>
          <MDBox marginTop={3} marginBottom={4}>
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName} (${option.email || "No Email Available"})`
              }
              value={contacts.find((c) => c.id === selectedContactId) || null}
              onChange={(event, newValue) => {
                setSelectedContactId(newValue ? newValue.id : null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Contact" variant="outlined" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </MDBox>

          <MDBox display="flex" flexDirection={"column"} mb={1} gap={1}>
            <Typography variant="h1" sx={{ fontSize: 14 }}>
              BANKS
            </Typography>
            <Typography sx={{ fontSize: 14 }}>
              Routing Number / Account Number{" "}
              <Typography
                component="span"
                sx={{ color: "green", fontStyle: "italic", fontSize: 14 }}
              >
                (optional)
              </Typography>
            </Typography>
          </MDBox>
          {bankEdit ? (
            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                fullWidth
                label="Routing Number"
                value={routing}
                onChange={(e: any) => setRouting(e.target.value)}
              />
              <MDInput
                fullWidth
                label="Account Number"
                value={account}
                onChange={(e: any) => setAccount(e.target.value)}
              />
              <MDBox display={"flex"}>
                <MDButton
                  startIcon={<Save sx={{ fontSize: "25 px" }} />}
                  onClick={() => {
                    setBankEdit(false);
                  }}
                />

                <MDButton
                  startIcon={<Close sx={{ fontSize: "25 px", color: "red" }} />}
                  onClick={() => {
                    setAccount("");
                    setRouting("");
                    setBankEdit(false);
                  }}
                />
              </MDBox>
            </MDBox>
          ) : (
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography sx={{ fontSize: 18 }}>
                {routing}
                {routing && account && " / "}
                {account}
              </Typography>
              <Button onClick={() => setBankEdit(true)}>Edit</Button>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="contained"
            color="error"
            onClick={() => {
              setShowDialog(false);
              setBankEdit(false);
              setAccount("");
              setRouting("");
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!selectedContactId || submitting}
          >
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DecisionLogicTab;
